import React, { useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import MultiSelectDropdown, { type Option } from './MultiSelectDropdown'
import { AddServiceDialog } from './AddServiceDialog'
import { franchiseRegions as REGION_OPTIONS } from '@/lib/constants'
import { useTags } from '../../hooks/useGalleryTags'
import { useServices } from '../../hooks/useServices'

export interface GalleryFiltersProps {
  search: string
  regions: string[]
  statuses: string[]
  tags: string[]
  services: string[]
  sortBy: 'created_on' | 'title'
  sortDir: 'asc' | 'desc'
  onChange: (next: Partial<Omit<GalleryFiltersProps, 'onChange'>>) => void
}

export default function GalleryFilters(props: GalleryFiltersProps) {
  // Partial implementation: search + sort + multi-selects for regions/statuses/tags
  // Remaining Requirements:
  // - [ ] Chip display + clear-all in page container using FilterChips
  // - [ ] A11y labels for inputs (using <Label/>)
  const tRef = useRef<number | undefined>()
  const onSearchChange = (value: string) => {
    if (tRef.current) window.clearTimeout(tRef.current)
    tRef.current = window.setTimeout(() => props.onChange({ search: value }), 300)
  }
  const regionOptions: Option[] = useMemo(
    () => (REGION_OPTIONS || []).map((r) => ({ value: String(r.value), label: r.label })),
    []
  )
  const statusOptions: Option[] = useMemo(
    () => [
      { value: 'approved', label: 'Approved' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'pending', label: 'Pending' },
    ],
    []
  )
  const { data: tags = [] } = useTags()
  const tagOptions: Option[] = useMemo(
    () => tags.map(t => ({ value: String(t.id), label: t.name })),
    [tags]
  )

  // Services for gallery categorization (masonry service types)
  const { data: services = [] } = useServices()
  const serviceOptions: Option[] = useMemo(
    () => services.map(s => ({ value: s.id, label: s.title })),
    [services]
  )

  // Add Service dialog state
  const [showAddService, setShowAddService] = useState(false)

  // When a new service is created, automatically add it to the filter
  const handleServiceCreated = (serviceId: string) => {
    props.onChange({ services: [...props.services, serviceId] })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
        <div className="relative flex-1">
          <Input
            placeholder="Search galleries..."
            defaultValue={props.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <Select value={props.sortBy} onValueChange={(v) => props.onChange({ sortBy: v as any })}>
            <SelectTrigger className="flex-1 lg:w-40 h-10">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_on">Created</SelectItem>
              <SelectItem value="title">Title</SelectItem>
            </SelectContent>
          </Select>
          <Select value={props.sortDir} onValueChange={(v) => props.onChange({ sortDir: v as any })}>
            <SelectTrigger className="w-24 lg:w-32 h-10">
              <SelectValue placeholder="Dir" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Desc</SelectItem>
              <SelectItem value="asc">Asc</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
        <MultiSelectDropdown
          label="Regions"
          options={regionOptions}
          selected={props.regions}
          onChange={(v) => props.onChange({ regions: v })}
          className="w-full sm:w-auto sm:min-w-[140px]"
        />
        <MultiSelectDropdown
          label="Statuses"
          options={statusOptions}
          selected={props.statuses}
          onChange={(v) => props.onChange({ statuses: v })}
          className="w-full sm:w-auto sm:min-w-[140px]"
        />
        <MultiSelectDropdown
          label="Tags"
          options={tagOptions}
          selected={props.tags}
          onChange={(v) => props.onChange({ tags: v })}
          className="w-full sm:w-auto sm:min-w-[140px]"
        />
        <div className="flex items-center gap-1 w-full sm:w-auto sm:min-w-[180px] col-span-2 sm:col-span-1">
          <MultiSelectDropdown
            label="Services"
            options={serviceOptions}
            selected={props.services}
            onChange={(v) => props.onChange({ services: v })}
            className="flex-1 sm:w-auto"
          />
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => setShowAddService(true)}
            title="Add new service"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Add Service Dialog */}
      <AddServiceDialog
        open={showAddService}
        onOpenChange={setShowAddService}
        onCreated={handleServiceCreated}
      />
    </div>
  )
}
